import { useState, useEffect, useRef, useCallback } from "react";
import {
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
} from "@mui/material";
import { SearchIcon, User, LineChart, X, Trash2 } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { QueryContact, QuerySecurity } from "@/api/queries/types";
import { useGetSavedQuery2 } from "@/api/queries/useGetSavedQuery";
import { Button } from "@/components/Button/Button";
import { InputWithDebounce } from "@/components/input-with-debounce";
import { useLocalStorageStore } from "@/hooks/useLocalStorageStore";
import { cn } from "@/lib/utils";
import { useDocProducerUI } from "@/providers/DocProducerProvider";

// ============================================================================
// TYPES
// ============================================================================

interface SearchItem {
  id: number;
  name: string;
  contactId: string;
}

interface SecuritySearchItem {
  id: number;
  name: string;
  isin_code: string;
}

// ============================================================================
// LOCAL STORAGE UTILITIES
// ============================================================================

const getSearchHistory = (): SearchItem[] => {
  const history = localStorage.getItem("searchHistory");
  return history ? JSON.parse(history) : [];
};

const saveSearch = (contact: SearchItem) => {
  const history = getSearchHistory();
  const filtered = history.filter((c) => c.id !== contact.id);
  const newHistory = [contact, ...filtered].slice(0, 10);
  localStorage.setItem("searchHistory", JSON.stringify(newHistory));
};

const removeSearchHistoryItem = (id: number) => {
  const history = getSearchHistory();
  const filtered = history.filter((c) => c.id !== id);
  localStorage.setItem("searchHistory", JSON.stringify(filtered));
};

const deleteSearchHistory = () => {
  localStorage.removeItem("searchHistory");
};

const getSecuritySearchHistory = (): SecuritySearchItem[] => {
  const history = localStorage.getItem("securitySearchHistory");
  return history ? JSON.parse(history) : [];
};

const saveSecuritySearch = (security: SecuritySearchItem) => {
  const history = getSecuritySearchHistory();
  const filtered = history.filter((s) => s.id !== security.id);
  const newHistory = [security, ...filtered].slice(0, 10);
  localStorage.setItem("securitySearchHistory", JSON.stringify(newHistory));
};

const removeSecuritySearchHistoryItem = (id: number) => {
  const history = getSecuritySearchHistory();
  const filtered = history.filter((s) => s.id !== id);
  localStorage.setItem("securitySearchHistory", JSON.stringify(filtered));
};

const deleteSecuritySearchHistory = () => {
  localStorage.removeItem("securitySearchHistory");
};

// ============================================================================
// SUBCOMPONENTS
// ============================================================================

const EmptyState = ({
  message,
  icon: Icon = SearchIcon,
}: {
  message: string;
  icon?: React.ElementType;
}) => (
  <div className="flex flex-col items-center justify-center py-12 gap-3 text-muted-foreground/50">
    <Icon className="size-12 stroke-[1]" />
    <p className="text-sm">{message}</p>
  </div>
);

const LoadingState = () => (
  <div className="flex items-center justify-center py-8">
    <div className="size-6 border-2 border-muted-foreground/20 border-t-primary rounded-full animate-spin" />
  </div>
);

const SectionHeader = ({ label }: { label: string }) => (
  <div className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider px-3 py-2">
    {label}
  </div>
);

const ColumnHeader = ({
  title,
  icon,
  showClear,
  onClear,
}: {
  title: string;
  icon: React.ReactNode;
  showClear: boolean;
  onClear: () => void;
}) => (
  <div className="flex items-center justify-between px-4 py-2.5 border-b bg-muted/20">
    <div className="flex items-center gap-2">
      {icon}
      <h3 className="text-sm font-semibold">{title}</h3>
    </div>
    {showClear && (
      <Button
        variant="ghost"
        size="sm"
        onClick={onClear}
        className="h-7 px-2 text-xs"
      >
        <Trash2 className="size-3 mr-1" />
        Clear
      </Button>
    )}
  </div>
);

// ============================================================================
// ROW COMPONENTS
// ============================================================================

const ContactRow = ({
  contact,
  onRemoveClick,
  onClick,
  isSelected = false,
}: {
  contact: QueryContact | SearchItem;
  onRemoveClick?: () => void;
  onClick: () => void;
  isSelected?: boolean;
}) => {
  const contactId = "contactId" in contact ? contact.contactId : null;
  const rowRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (isSelected && rowRef.current) {
      rowRef.current.scrollIntoView({ behavior: "smooth", block: "nearest" });
    }
  }, [isSelected]);

  return (
    <div
      ref={rowRef}
      className={cn(
        "group w-full flex items-center gap-2.5 px-3 py-1.5 hover:bg-accent/80 transition-colors text-left border-b border-border/50 last:border-0 cursor-pointer",
        isSelected && "bg-accent ring-2 ring-primary/50"
      )}
      onClick={onClick}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onClick();
        }
      }}
    >
      <div className="size-7 rounded-md bg-primary/10 flex items-center justify-center flex-shrink-0">
        <User className="size-3.5 text-primary" strokeWidth={2} />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium truncate leading-tight">
          {contact.name}
        </p>
        {contactId && (
          <p className="text-xs text-muted-foreground leading-none">
            {contactId}
          </p>
        )}
      </div>

      {onRemoveClick && (
        <Button
          role="button"
          variant="icon"
          size="sm"
          tabIndex={0}
          onClick={(e) => {
            e.stopPropagation();
            onRemoveClick();
          }}
          className="opacity-0 group-hover:opacity-100 size-5 rounded hover:bg-destructive/10 flex items-center justify-center text-muted-foreground hover:text-destructive flex-shrink-0 cursor-pointer"
        >
          <X className="size-3" />
        </Button>
      )}
    </div>
  );
};

const SecurityRow = ({
  security,
  onRemoveClick,
  onClick,
  isSelected = false,
}: {
  security: QuerySecurity | SecuritySearchItem;
  onRemoveClick?: () => void;
  onClick: () => void;
  isSelected?: boolean;
}) => {
  const className = "class2name" in security ? security.class2name : null;
  const classCode = "class2code" in security ? security.class2code : null;
  const rowRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (isSelected && rowRef.current) {
      rowRef.current.scrollIntoView({ behavior: "smooth", block: "nearest" });
    }
  }, [isSelected]);

  // Map security type codes to colors
  const getSecurityTypeColor = (code: string | null) => {
    switch (code) {
      case "B": // Bond
        return "bg-amber-500/10 text-amber-600 dark:text-amber-400 border-amber-500/20";
      case "C": // Cash
        return "bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border-emerald-500/20";
      case "EF": // Equity fund
        return "bg-blue-500/10 text-blue-600 dark:text-blue-400 border-blue-500/20";
      case "FI": // Fixed income fund
        return "bg-indigo-500/10 text-indigo-600 dark:text-indigo-400 border-indigo-500/20";
      case "O": // Other
        return "bg-slate-500/10 text-slate-600 dark:text-slate-400 border-slate-500/20";
      case "S": // Stock
        return "bg-purple-500/10 text-purple-600 dark:text-purple-400 border-purple-500/20";
      default:
        return "bg-muted text-muted-foreground border-border";
    }
  };

  return (
    <div
      ref={rowRef}
      className={cn(
        "group w-full flex items-center gap-2.5 px-3 py-1.5 hover:bg-accent/80 transition-colors text-left border-b border-border/50 last:border-0 cursor-pointer",
        isSelected && "bg-accent ring-2 ring-primary/50"
      )}
      onClick={onClick}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onClick();
        }
      }}
    >
      <div className="size-7 rounded-md bg-blue-500/10 flex items-center justify-center flex-shrink-0">
        <LineChart className="size-3.5 text-blue-500" strokeWidth={2} />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium truncate leading-tight">
          {security.name}
        </p>
        <div className="flex items-center gap-1.5 text-xs text-muted-foreground leading-none justify-between w-full">
          <span className="font-mono">{security.isin_code}</span>
          {classCode && (
            <span
              className={cn(
                "px-1 py-[1px] rounded-sm text-[8px] font-semibold border flex-shrink-0",
                getSecurityTypeColor(classCode)
              )}
            >
              {className}
            </span>
          )}
        </div>
      </div>

      {onRemoveClick && (
        <Button
          role="button"
          variant="icon"
          size="sm"
          tabIndex={0}
          onClick={(e) => {
            e.stopPropagation();
            onRemoveClick();
          }}
          className="opacity-0 group-hover:opacity-100 size-5 rounded hover:bg-destructive/10 flex items-center justify-center text-muted-foreground hover:text-destructive flex-shrink-0 cursor-pointer"
        >
          <X className="size-3" />
        </Button>
      )}
    </div>
  );
};

// ============================================================================
// MAIN COMPONENT
// ============================================================================

export const Search = () => {
  const navigate = useNavigate();
  const [openContacts, setOpenContacts] = useLocalStorageStore();

  // UI State
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [searchLoading, setSearchLoading] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [focusedColumn, setFocusedColumn] = useState<"contacts" | "securities">(
    "contacts"
  );

  // History State
  const [contactHistory, setContactHistory] = useState<SearchItem[]>([]);
  const [securityHistory, setSecurityHistory] = useState<SecuritySearchItem[]>(
    []
  );

  // Data Fetching
  const { data: contacts, loading: contactsLoading } =
    useGetSavedQuery2<QueryContact>("advPortalContactsSimple", {
      limit: 3000,
      contactName: "%",
    });

  const { data: securities, loading: securitiesLoading } =
    useGetSavedQuery2<QuerySecurity>("advPortalInstrumentsSimple", {});

  // Prefetch the filtered security list for DocProducer.
  useGetSavedQuery2<QuerySecurity>("advPortalInstruments", {
    status: "ACTIVE",
    typeCode: "%",
    currencyCode: "%",
    securityName: "%",
    tag: "%Capguard-Yes%",
  });

  // Load history when dialog opens
  useEffect(() => {
    if (open) {
      setContactHistory(getSearchHistory());
      setSecurityHistory(getSecuritySearchHistory());
      setSearch("");
    }
  }, [open]);

  // Keyboard shortcut
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "/" && !event.ctrlKey && !event.metaKey) {
        const target = event.target as HTMLElement;
        if (target.tagName !== "INPUT" && target.tagName !== "TEXTAREA") {
          setOpen((prev) => !prev);
          event.preventDefault();
        }
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, []);

  // Filter logic
  const isSearching = search.trim().length > 0;

  const filteredContacts = isSearching
    ? contacts
        ?.filter((contact) => {
          const searchLower = search.toLowerCase();
          return (
            contact.name.toLowerCase().includes(searchLower) ||
            contact.email?.toLowerCase().includes(searchLower) ||
            String(contact.contactId).includes(searchLower)
          );
        })
        .sort((a, b) => a.name.localeCompare(b.name))
        .slice(0, 50)
    : contacts
        ?.slice()
        .sort((a, b) => a.name.localeCompare(b.name))
        .slice(0, 10) || [];

  const filteredSecurities = isSearching
    ? securities
        ?.filter((security) => {
          const searchLower = search.toLowerCase();
          return (
            security.name.toLowerCase().includes(searchLower) ||
            security.isin_code?.toLowerCase().includes(searchLower) ||
            security.class2name?.toLowerCase().includes(searchLower)
          );
        })
        .sort((a, b) => a.name.localeCompare(b.name))
        .slice(0, 50)
    : securities
        ?.slice()
        .sort((a, b) => a.name.localeCompare(b.name))
        .slice(0, 10) || [];

  // Handlers
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const addContactToRecent = useCallback(
    (contact: { id: number; name: string }) => {
      const exists = openContacts.some((c) => c.id === contact.id);
      if (exists) {
        const filtered = openContacts.filter((c) => c.id !== contact.id);
        setOpenContacts([contact, ...filtered]);
      } else {
        setOpenContacts([...openContacts, contact]);
      }
    },
    [openContacts, setOpenContacts]
  );

  const { openDialog } = useDocProducerUI();

  const handleContactClick = useCallback(
    (contact: QueryContact | SearchItem) => {
      const contactData = {
        name: contact.name,
        id: contact.id,
        contactId: "contactId" in contact ? contact.contactId : "",
      };
      saveSearch(contactData);
      addContactToRecent({ id: contact.id, name: contact.name });
      setOpen(false);
      navigate(`/contacts/${contact.id}`);
    },
    [navigate, addContactToRecent]
  );

  const handleContactTabClick = (contact: QueryContact | SearchItem) => {
    const contactData = {
      name: contact.name,
      id: contact.id,
      contactId: "contactId" in contact ? contact.contactId : "",
    };

    saveSearch(contactData);
    addContactToRecent({ id: contact.id, name: contact.name });
    setOpen(false);
    navigate(`/contacts/${contact.id}`);
  };

  const handleSecurityClick = useCallback(
    (security: QuerySecurity | SecuritySearchItem) => {
      const securityData = {
        name: security.name,
        id: security.id,
        isin_code: security.isin_code,
      };

      saveSecuritySearch(securityData);
      setOpen(false);
      navigate(`/securities/${security.id}`);
    },
    [navigate]
  );

  const handleClearContactHistory = () => {
    deleteSearchHistory();
    setContactHistory([]);
  };

  const handleClearSecurityHistory = () => {
    deleteSecuritySearchHistory();
    setSecurityHistory([]);
  };

  const handleRemoveContactFromHistory = (id: number) => {
    removeSearchHistoryItem(id);
    setContactHistory((prev) => prev.filter((c) => c.id !== id));
  };

  const handleRemoveSecurityFromHistory = (id: number) => {
    removeSecuritySearchHistoryItem(id);
    setSecurityHistory((prev) => prev.filter((s) => s.id !== id));
  };

  const showContactHistory = contactHistory.length > 0 && !isSearching;
  const showSecurityHistory = securityHistory.length > 0 && !isSearching;

  // Get current items based on focused column
  const currentContactItems = showContactHistory
    ? contactHistory
    : filteredContacts;
  const currentSecurityItems = showSecurityHistory
    ? securityHistory
    : filteredSecurities;
  const maxContactIndex = currentContactItems.length - 1;
  const maxSecurityIndex = currentSecurityItems.length - 1;

  // Handle keyboard navigation
  useEffect(() => {
    if (!open) return;

    const handleKeyDown = (event: KeyboardEvent) => {
      const target = event.target as HTMLElement;

      // Don't interfere if typing in input
      if (
        target.tagName === "INPUT" &&
        event.key !== "ArrowDown" &&
        event.key !== "ArrowUp" &&
        event.key !== "Enter" &&
        event.key !== "ArrowLeft" &&
        event.key !== "ArrowRight"
      ) {
        return;
      }

      switch (event.key) {
        case "ArrowDown":
          event.preventDefault();
          if (focusedColumn === "contacts" && maxContactIndex >= 0) {
            setSelectedIndex((prev) => Math.min(prev + 1, maxContactIndex));
          } else if (focusedColumn === "securities" && maxSecurityIndex >= 0) {
            setSelectedIndex((prev) => Math.min(prev + 1, maxSecurityIndex));
          }
          break;

        case "ArrowUp":
          event.preventDefault();
          setSelectedIndex((prev) => Math.max(prev - 1, 0));
          break;

        case "ArrowLeft":
          event.preventDefault();
          setFocusedColumn("contacts");
          setSelectedIndex(0);
          break;

        case "ArrowRight":
          event.preventDefault();
          setFocusedColumn("securities");
          setSelectedIndex(0);
          break;

        case "Enter":
          event.preventDefault();
          if (
            focusedColumn === "contacts" &&
            currentContactItems[selectedIndex]
          ) {
            handleContactClick(currentContactItems[selectedIndex]);
          } else if (
            focusedColumn === "securities" &&
            currentSecurityItems[selectedIndex]
          ) {
            handleSecurityClick(currentSecurityItems[selectedIndex]);
          }
          break;
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [
    open,
    selectedIndex,
    focusedColumn,
    maxContactIndex,
    maxSecurityIndex,
    currentContactItems,
    currentSecurityItems,
    handleContactClick,
    handleSecurityClick,
  ]);

  // Reset selection when search changes
  useEffect(() => {
    setSelectedIndex(0);
  }, [search, focusedColumn]);

  const inputRef = useRef<HTMLInputElement>(null);

  return (
    <>
      <Button
        onClick={() => setOpen(true)}
        size="sm"
        variant="outline"
        className="bg-muted/50 hover:bg-muted normal-case"
      >
        <div className="flex justify-start gap-4 items-center w-52 text-muted-foreground">
          <SearchIcon className="size-5" />
          <div className="flex items-center gap-2 text-sm">
            Type
            <kbd className="border rounded-md size-6 flex items-center justify-center text-xs font-semibold bg-background">
              /
            </kbd>
            to search
          </div>
        </div>
      </Button>

      <Dialog
        open={open}
        onClose={() => setOpen(false)}
        maxWidth="lg"
        fullWidth
        onTransitionEnter={() => {
          inputRef.current?.focus();
        }}
      >
        <DialogTitle sx={{ display: "none" }}>Search</DialogTitle>
        <DialogContent
          sx={{ padding: 0 }}
          className="p-2 h-[80vh] flex flex-col relative"
        >
          {/* Close button */}
          <Button
            onClick={() => setOpen(false)}
            className="absolute right-2 top-2 z-10"
            size="md"
            variant="icon"
            aria-label="Close search dialog"
          >
            <X className="size-5" />
          </Button>
          {/* Fixed Header with Search */}
          <div className="flex-shrink-0 p-2 pr-14 border-b">
            <div className="relative">
              {searchLoading ? (
                <div className="absolute left-3 top-1/2 -translate-y-1/2 size-4 text-muted-foreground pointer-events-none">
                  <CircularProgress size="15px" />
                </div>
              ) : (
                <SearchIcon
                  className="absolute left-3 top-1/2 -translate-y-1/2 size-4 text-muted-foreground pointer-events-none"
                  strokeWidth={2}
                />
              )}
              <InputWithDebounce
                ref={inputRef}
                className="w-full pl-10 h-10 rounded-lg"
                value={search}
                withLoader
                onLoadingChange={setSearchLoading}
                onChange={(val) => setSearch(String(val))}
                placeholder="Search contacts or securities..."
                autoFocus
              />
            </div>
          </div>

          {/* Scrollable Results */}
          <div className="flex-1 grid grid-cols-2 divide-x min-h-0">
            {/* Contacts Column */}
            <div className="flex flex-col min-h-0">
              <ColumnHeader
                title="Contacts"
                icon={<User className="size-4 text-primary" strokeWidth={2} />}
                showClear={showContactHistory}
                onClear={handleClearContactHistory}
              />

              <div className="overflow-y-auto">
                {contactsLoading ? (
                  <LoadingState />
                ) : (
                  <>
                    {showContactHistory && (
                      <>
                        <SectionHeader label="Recent" />
                        {contactHistory.map((contact, index) => (
                          <ContactRow
                            key={`history-${contact.id}`}
                            contact={contact}
                            onRemoveClick={() =>
                              handleRemoveContactFromHistory(contact.id)
                            }
                            onClick={() => handleContactClick(contact)}
                            isSelected={
                              focusedColumn === "contacts" &&
                              selectedIndex === index
                            }
                          />
                        ))}
                      </>
                    )}

                    {filteredContacts.length > 0 && (
                      <>
                        <SectionHeader
                          label={isSearching ? "Results" : "All Contacts"}
                        />
                        {filteredContacts.map((contact, index) => (
                          <ContactRow
                            key={`contact-${contact.id}`}
                            contact={contact}
                            onClick={() => handleContactClick(contact)}
                            isSelected={
                              focusedColumn === "contacts" &&
                              selectedIndex ===
                                (showContactHistory
                                  ? contactHistory.length + index
                                  : index)
                            }
                          />
                        ))}
                      </>
                    )}

                    {isSearching && filteredContacts.length === 0 && (
                      <EmptyState message="No contacts found" icon={User} />
                    )}
                  </>
                )}
              </div>
            </div>

            {/* Securities Column */}
            <div className="flex flex-col min-h-0">
              <ColumnHeader
                title="Securities"
                icon={
                  <LineChart className="size-4 text-blue-500" strokeWidth={2} />
                }
                showClear={showSecurityHistory}
                onClear={handleClearSecurityHistory}
              />

              <div className="overflow-y-auto">
                {securitiesLoading ? (
                  <LoadingState />
                ) : (
                  <>
                    {showSecurityHistory && (
                      <>
                        <SectionHeader label="Recent" />
                        {securityHistory.map((security, index) => (
                          <SecurityRow
                            key={`history-${security.id}`}
                            security={security}
                            onRemoveClick={() =>
                              handleRemoveSecurityFromHistory(security.id)
                            }
                            onClick={() => handleSecurityClick(security)}
                            isSelected={
                              focusedColumn === "securities" &&
                              selectedIndex === index
                            }
                          />
                        ))}
                      </>
                    )}

                    {filteredSecurities.length > 0 && (
                      <>
                        <SectionHeader
                          label={isSearching ? "Results" : "All Securities"}
                        />
                        {filteredSecurities.map((security, index) => (
                          <SecurityRow
                            key={`security-${security.id}`}
                            security={security}
                            onClick={() => handleSecurityClick(security)}
                            isSelected={
                              focusedColumn === "securities" &&
                              selectedIndex ===
                                (showSecurityHistory
                                  ? securityHistory.length + index
                                  : index)
                            }
                          />
                        ))}
                      </>
                    )}

                    {isSearching && filteredSecurities.length === 0 && (
                      <EmptyState
                        message="No securities found"
                        icon={LineChart}
                      />
                    )}
                  </>
                )}
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};
